import React from 'react';
import { Layout, Typography, Space, Menu, Avatar, Dropdown, Button, Tooltip, Tag } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import logoImg from '/assets/images/tarastb.png';
import { 
  DashboardOutlined, 
  VideoCameraOutlined, 
  MonitorOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
  BellOutlined,
  SunOutlined,
  MoonOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { getAuth, setAuth } from '../api/clientService';
import { useTheme } from '@/contexts/ThemeContext';

const { Header: AntHeader } = Layout;
const { Title } = Typography;

interface HeaderProps {
  title?: string;
  children?: React.ReactNode;
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ title, children, className }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const auth = getAuth();
  const email = auth?.email || auth?.userId || '';

  // ✅ ADD: Get app version from package.json
  const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';

  const menuItems = [
    { key: '/stream-management', icon: <VideoCameraOutlined />, label: 'Streaming Panel' },
    { key: '/device-management', icon: <MonitorOutlined />, label: 'Device Management' },
  ];

  const handleMenuClick = (e: any) => {
    navigate(e.key);
  };

  const userMenuItems = [
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true },
  ];

  const handleUserMenuClick = ({ key }: any) => {
    switch (key) {
      case 'logout':
        setAuth(null);
        window.dispatchEvent(new Event("auth:logout"));
        navigate('/login');
        break;
      default:
        break;
    }
  };

  return (
    <AntHeader
      className={className}
      style={{
        background: 'var(--color-card)',
        padding: '0 24px',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div className="flex items-center mr-8">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center">
            <img src={logoImg} alt="TARA STB" />
          </div>
          <div className="text-left ml-3">
            <h1 className="text-2xl font-bold text-foreground" style={{ margin: 0 }}>TARA STB</h1>
            <p className="text-sm text-muted-foreground -mt-1" style={{ margin: 0 }}>Operator Panel</p>
          </div>
        </div>
        <Menu
          mode="horizontal"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            border: 'none',
            backgroundColor: 'transparent',
            flex: 1,
            minWidth: 0,
          }}
        />
      </div>

      <Space size="middle">
        {children}

        {/* ✅ ADD: Version Tag (visible in header) */}
        <Tag 
          color="blue" 
          style={{ 
            marginRight: 8,
            fontSize: '11px',
            padding: '2px 6px',
            lineHeight: '16px'
          }}
        >
          v{appVersion}
        </Tag>

        {/* Theme Toggle Button */}
        <Tooltip title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}>
          <Button
            type="text"
            icon={theme === 'light' ? <MoonOutlined /> : <SunOutlined />}
            onClick={toggleTheme}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
            }}
          />
        </Tooltip>

        {/* User Menu */}
        <Dropdown
          menu={{
            items: userMenuItems,
            onClick: handleUserMenuClick,
          }}
          placement="bottomRight"
          arrow
        >
          <Space style={{ cursor: 'pointer' }}>
            <Avatar icon={<UserOutlined />} />
            <span style={{ color: 'var(--color-foreground)' }}>
              {email || 'User'}
            </span>
          </Space>
        </Dropdown>
      </Space>
    </AntHeader>
  );
};

export default Header;
